import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { UserMenu } from '../components/UserMenu';
import { NotificationBell } from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Users, Crown, Shield, UserPlus, Trash2, ArrowLeft,
  Copy, Mail, ChevronDown, Building2, Edit2, Check, X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ROLE_CONFIG = {
  owner: { label: 'Owner', icon: Crown, color: 'text-amber-400' },
  admin: { label: 'Admin', icon: Shield, color: 'text-blue-400' },
  agent: { label: 'Agent', icon: Users, color: 'text-slate-400' },
};

const MemberRow = ({ member, currentRole, onRoleChange, onRemove }) => {
  const cfg = ROLE_CONFIG[member.org_role] || ROLE_CONFIG.agent;
  const Icon = cfg.icon;
  const isOwner = member.org_role === 'owner';
  const canManage = currentRole === 'owner' || (currentRole === 'admin' && member.org_role === 'agent');

  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-slate-800/40 border border-slate-700/50" data-testid={`member-row-${member.user_id}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium">
          {member.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-100">{member.name}</p>
          <p className="text-xs text-slate-400">{member.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {canManage && !isOwner ? (
          <Select value={member.org_role} onValueChange={(val) => onRoleChange(member.user_id, val)}>
            <SelectTrigger className="w-28 h-8 text-xs bg-slate-700/50 border-slate-600" data-testid={`role-select-${member.user_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="agent">Agent</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className={`flex items-center gap-1 text-xs font-medium ${cfg.color}`}>
            <Icon className="h-3.5 w-3.5" />
            {cfg.label}
          </span>
        )}
        {canManage && !isOwner && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={() => onRemove(member.user_id, member.name)}
            data-testid={`remove-member-${member.user_id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

const InviteRow = ({ invite, onCancel }) => (
  <div className="flex items-center justify-between py-2.5 px-4 rounded-lg bg-slate-800/20 border border-dashed border-slate-700/50" data-testid={`invite-row-${invite.invite_id}`}>
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-full bg-slate-700/50 flex items-center justify-center">
        <Mail className="h-4 w-4 text-slate-500" />
      </div>
      <div>
        <p className="text-sm text-slate-300">{invite.email}</p>
        <p className="text-xs text-slate-500">Invited as {invite.role} &middot; Pending</p>
      </div>
    </div>
    <Button variant="ghost" size="sm" className="text-xs text-slate-400 hover:text-red-400"
      onClick={() => onCancel(invite.invite_id)}
      data-testid={`cancel-invite-${invite.invite_id}`}>
      Cancel
    </Button>
  </div>
);

export default function TeamManagementPage() {
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const [orgData, setOrgData] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [currentRole, setCurrentRole] = useState('owner');
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('agent');
  const [inviting, setInviting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [orgName, setOrgName] = useState('');

  useEffect(() => { fetchOrg(); }, []);

  const fetchOrg = async () => {
    try {
      const res = await apiClient.get('/organizations/me');
      setOrgData(res.data.organization);
      setMembers(res.data.members);
      setInvites(res.data.pending_invites);
      setCurrentRole(res.data.current_user_role);
      setOrgName(res.data.organization.name);
    } catch (err) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      await apiClient.post('/organizations/invite', { email: inviteEmail, role: inviteRole });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await apiClient.put(`/organizations/members/${userId}/role`, { role });
      toast.success('Role updated');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemove = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the team? They will be moved to their own workspace.`)) return;
    try {
      await apiClient.delete(`/organizations/members/${userId}`);
      toast.success(`${name} removed`);
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await apiClient.delete(`/organizations/invites/${inviteId}`);
      toast.success('Invite cancelled');
      fetchOrg();
    } catch (err) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleSaveName = async () => {
    try {
      await apiClient.put('/organizations/me', { name: orgName });
      toast.success('Organization name updated');
      setEditingName(false);
      fetchOrg();
    } catch (err) {
      toast.error('Failed to update name');
    }
  };

  const isTeamPlan = orgData?.plan_type === 'team';
  const seatCount = members.length + invites.length;
  const maxSeats = orgData?.max_seats || 1;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-slate-400">Loading team...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="team-management-page">
      <div className="noise-texture" />

      <header className="sticky top-0 z-50 glassmorphic">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Team Management</h1>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-3xl space-y-8">
        {/* Org Info Card */}
        <div className="rounded-xl bg-slate-800/30 border border-slate-700/50 p-6" data-testid="org-info-card">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Building2 className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} className="h-9 bg-slate-700/50 border-slate-600 text-sm" data-testid="org-name-input" />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-green-400" onClick={handleSaveName} data-testid="save-org-name"><Check className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" onClick={() => { setEditingName(false); setOrgName(orgData?.name || ''); }} data-testid="cancel-org-name"><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-slate-100">{orgData?.name}</h2>
                  {(currentRole === 'owner' || currentRole === 'admin') && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" onClick={() => setEditingName(true)} data-testid="edit-org-name"><Edit2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  orgData?.plan_type === 'team' ? 'bg-violet-500/20 text-violet-300' :
                  orgData?.plan_type === 'pro' ? 'bg-blue-500/20 text-blue-300' :
                  'bg-slate-500/20 text-slate-300'
                }`} data-testid="plan-badge">
                  {orgData?.plan_type?.charAt(0).toUpperCase() + orgData?.plan_type?.slice(1)} Plan
                </span>
                <span className="text-xs text-slate-500">{seatCount} / {maxSeats} seats</span>
              </div>
            </div>
          </div>
        </div>

        {/* Members */}
        <div data-testid="members-section">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Members ({members.length})</h3>
          <div className="space-y-2">
            {members.map((m) => (
              <MemberRow key={m.user_id} member={m} currentRole={currentRole} onRoleChange={handleRoleChange} onRemove={handleRemove} />
            ))}
          </div>
        </div>

        {/* Pending Invites */}
        {invites.length > 0 && (
          <div data-testid="invites-section">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Pending Invites ({invites.length})</h3>
            <div className="space-y-2">
              {invites.map((inv) => (
                <InviteRow key={inv.invite_id} invite={inv} onCancel={handleCancelInvite} />
              ))}
            </div>
          </div>
        )}

        {/* Invite Form - Only for team plan owners/admins */}
        {isTeamPlan && (currentRole === 'owner' || currentRole === 'admin') ? (
          <div className="rounded-xl bg-slate-800/30 border border-slate-700/50 p-6" data-testid="invite-form">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Invite New Member</h3>
            <div className="flex gap-3">
              <Input
                placeholder="email@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="flex-1 bg-slate-700/50 border-slate-600"
                data-testid="invite-email-input"
              />
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="w-28 bg-slate-700/50 border-slate-600" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleInvite} disabled={inviting || !inviteEmail || seatCount >= maxSeats}
                className="bg-violet-600 hover:bg-violet-500" data-testid="send-invite-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                {inviting ? 'Sending...' : 'Invite'}
              </Button>
            </div>
            {seatCount >= maxSeats && (
              <p className="text-xs text-amber-400 mt-2">All seats are filled. Upgrade to add more members.</p>
            )}
          </div>
        ) : !isTeamPlan ? (
          <div className="rounded-xl bg-slate-800/20 border border-dashed border-slate-700/50 p-8 text-center" data-testid="upgrade-prompt">
            <Users className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <h3 className="text-base font-medium text-slate-300 mb-1">Unlock Team Collaboration</h3>
            <p className="text-sm text-slate-500 mb-4">Upgrade to a Team plan to invite agents, share content, and manage your brokerage.</p>
            <Button variant="outline" className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
              onClick={() => navigate('/subscription')} data-testid="upgrade-to-team-btn">
              Upgrade to Team
            </Button>
          </div>
        ) : null}
      </main>
    </div>
  );
}
